/**
 * PATH: packages/ui/src/components/settings/role-management-tab/MembersTable.tsx
 * PURPOSE: Renders the members table with role badges and role-change selectors
 *
 * WHY: Extracted from RoleManagementTab.tsx (~110 lines of JSX) to keep the
 *      orchestrator file under 300 lines.
 *
 * FLOW:
 *   ┌───────────┐    ┌──────────────┐    ┌────────────────┐
 *   │ members[] │───▶│ Table Rows   │───▶│ Role <Select>  │
 *   └───────────┘    └──────────────┘    └────────────────┘
 *
 * DEPENDENCIES:
 *   - ../ui/select: Shadcn Select primitives
 *   - ./role-management-types: shared types + icon/color maps
 */

import { RefreshCw, User, Users } from 'lucide-react'
import { cn } from '../../../lib/utils'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import type { SettingsUser } from '../../SettingsModal'
import type { MemberRole, SystemRole } from './role-management-types'
import { ROLE_ICONS, ROLE_COLORS, groupRolesByCategory } from './role-management-types'

// =============================================================================
// TYPES
// =============================================================================

interface MembersTableProps {
  members: MemberRole[]
  roles: SystemRole[]
  canManage: boolean
  savingUserId: number | null
  user?: SettingsUser | null
  isLoading: boolean
  onRoleChange: (userId: number, roleCode: string) => void
  /**
   * RBAC V2 MR 19 FE (2026-05-29) — current user's hierarchy_level, used
   * to decide whether to show the "Higher privilege" lock on each row.
   * Pre-MR-19 the lock fired whenever the member's `role.code` was not in
   * the assignable-roles allowlist (the 7 V2 canonical codes). That broke
   * for any legacy/transitional code that didn't make it into the V2 set
   * — e.g. Luc Volschenk on Bravada org 4 was on the LEGACY `site_worker`
   * code (hier=90, lower privilege than Vee at hier=10 owner) but the
   * dropdown locked and showed "Higher privilege" because his code wasn't
   * in the allowlist. Now the lock uses an actual hierarchy comparison.
   * `undefined` here means "we don't know" → permissive (allow dropdown,
   * BE will enforce).
   */
  currentUserHierarchy?: number
}

// =============================================================================
// COMPONENT
// =============================================================================

export function MembersTable({
  members,
  roles,
  canManage,
  savingUserId,
  user,
  isLoading,
  onRoleChange,
  currentUserHierarchy,
}: MembersTableProps) {
  if (isLoading) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="flex items-center justify-center p-8">
          <RefreshCw className="w-6 h-6 animate-spin text-teal-600" />
        </div>
      </div>
    )
  }

  if (members.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No team members found
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Member
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Current Role
            </th>
            {canManage && (
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Change Role
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
          {members.map(member => {
            const roleCode = member.role?.code || 'member'
            const Icon = ROLE_ICONS[roleCode] || User
            const colorClass = ROLE_COLORS[roleCode] || ROLE_COLORS.member
            const matchedRole = roles.find(r => r.code === roleCode)
            const isSaving = savingUserId === member.user_id
            const isCurrentUser = user?.email === member.email

            // RBAC V2 MR 19 FE (2026-05-29) — hierarchy-based privilege gate.
            // We lock the dropdown ONLY when we can prove the member outranks
            // the current user. "Outranks" means lower hierarchy_level (the
            // BE convention: owner=10, admin=20, manager=30, supervisor=50,
            // worker=80, subcontractor=150, client=180). If we don't know
            // EITHER number (e.g. legacy code not in our roles[] catalogue,
            // or current user's hierarchy wasn't resolvable), fall back to
            // PERMISSIVE — the BE's can_assign_role gate will reject the
            // PUT if the operator is overreaching, and the user will see
            // an error toast. Permissive-by-default beats false-locking
            // legitimate operators behind a UI mistake.
            const memberHierarchy = matchedRole?.hierarchy_level
            const isHigherPrivilege =
              typeof memberHierarchy === 'number' &&
              typeof currentUserHierarchy === 'number' &&
              memberHierarchy < currentUserHierarchy

            const isDropdownDisabled = isSaving || isCurrentUser || isHigherPrivilege

            return (
              <tr
                key={member.user_id}
                className={cn(
                  'transition-colors',
                  isCurrentUser && 'bg-teal-50/30 dark:bg-teal-900/10'
                )}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                        {(member.first_name?.[0] || member.email[0]).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        {member.first_name} {member.last_name}
                        {isCurrentUser && (
                          <span className="ml-2 text-xs text-teal-600 dark:text-teal-400">(You)</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {member.email}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border cursor-help',
                      colorClass
                    )}
                    title={matchedRole?.description || member.role?.name || 'No role assigned'}
                  >
                    <Icon className="w-3 h-3" />
                    {member.role?.name || 'No Role'}
                  </div>
                </td>
                {canManage && (
                  <td className="px-4 py-3 text-right">
                    {isDropdownDisabled && !isSaving ? (
                      <span className="text-xs text-slate-400 dark:text-slate-500 italic">
                        {isCurrentUser ? 'Cannot change own role' : 'Higher privilege'}
                      </span>
                    ) : (
                    <Select
                      value={roleCode}
                      onValueChange={(value) => onRoleChange(member.user_id, value)}
                      disabled={isSaving}
                    >
                      <SelectTrigger className="w-[180px] h-8 text-xs">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {groupRolesByCategory(roles).map(group => (
                          <SelectGroup key={group.label}>
                            <SelectLabel>{group.label}</SelectLabel>
                            {group.roles.map(role => (
                              <SelectItem
                                key={role.code}
                                value={role.code}
                                title={role.description}
                              >
                                <div className="flex items-center gap-2">
                                  {(() => {
                                    const RoleIcon = ROLE_ICONS[role.code] || User
                                    return <RoleIcon className="w-3 h-3" />
                                  })()}
                                  {role.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
